import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertOctagon, RefreshCw } from "lucide-react";

// Listens for the "arq:global-error" event dispatched by the axios interceptor
// (fired only for real system errors: 5xx + network failures) and shows a
// single reassuring modal. Validation errors (400/422) keep their inline toasts.
export default function GlobalErrorModal() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      // Never surface internal system-error details to clients/crew in the
      // public portals — those views handle failures on their own.
      const path = window.location.pathname || "";
      if (path.startsWith("/portal/") || path.startsWith("/crew-portal/")) return;
      setInfo(e.detail || {});
      setOpen(true);
    };
    window.addEventListener("arq:global-error", handler);
    return () => window.removeEventListener("arq:global-error", handler);
  }, []);

  const isNetwork = info?.isNetwork;
  const status = info?.status || 0;
  const baseRef = info?.reference || (isNetwork ? "NETWORK" : `ERR-${status}`);
  const ref = `${baseRef} · ${info?.at ? new Date(info.at).toLocaleTimeString() : ""}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md bg-[#11161F] border border-white/10 text-white" data-testid="global-error-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/15 border border-red-500/30 flex-shrink-0">
              <AlertOctagon className="w-5 h-5 text-red-400" />
            </span>
            <DialogTitle className="text-lg font-heading font-bold text-white">
              {isNetwork ? "We can't reach the server" : "Something went wrong on our end"}
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            An error occurred and the site administrator has been automatically notified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-1">
          <p className="text-sm text-slate-300 leading-relaxed">
            {isNetwork
              ? "We couldn't connect to complete that action. This is usually a brief network hiccup."
              : "We ran into an unexpected error and couldn't finish that action. This is on us, not you."}
          </p>
          <p className="text-sm text-slate-300 leading-relaxed">
            Our team has been <span className="text-white font-semibold">automatically alerted</span> and is already
            looking into it — there's nothing you need to report. We'll get it fixed as fast as we can and reach out
            directly if we need anything from you.
          </p>
          <div className="text-[11px] text-slate-500 font-mono bg-black/30 border border-white/5 rounded-md px-3 py-2" data-testid="global-error-ref">
            Reference: {ref}
          </div>
          <p className="text-xs text-slate-500">
            In the meantime, you can try again in a moment or reload the page.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => window.location.reload()}
            data-testid="global-error-reload-btn"
            className="border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white">
            <RefreshCw className="w-4 h-4 mr-2" /> Reload
          </Button>
          <Button onClick={() => setOpen(false)}
            data-testid="global-error-close-btn"
            className="bg-red-500/90 hover:bg-red-500 text-white">
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
